import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import {
  fetchWorkerDetail,
  fetchWorkflowsByProject,
  fetchWorkflowsByProgram,
  fetchProject,
  fetchProgram,
  deleteWorkflow,
} from "../../api/memberApi";
import WorkflowFormDialog from "./WorkflowFormDialog";

const STATUS_COLORS = {
  Planned: "default",
  Executing: "primary",
  Completed: "success",
  Evaluated: "secondary",
};

export default function WorkflowListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const notify = useNotification();

  const [worker, setWorker] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [actionPlans, setActionPlans] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [programId, setProgramId] = useState(null);
  const [loading, setLoading] = useState(true);

  // ダイアログ
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // null=追加, obj=編集
  const [deleteTarget, setDeleteTarget] = useState(null);

  const statusLabel = (status) => {
    const keyMap = {
      Planned: "workflow.status.planned",
      Executing: "workflow.status.executing",
      Completed: "workflow.status.completed",
      Evaluated: "workflow.status.evaluated",
    };
    const key = keyMap[status];
    return key ? intl.formatMessage({ id: key }) : status || "—";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  // ── データ読み込み ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const workerData = await fetchWorkerDetail(workerId);
      setWorker(workerData);

      let wfData = [];
      let pid = workerData.projectId || null;
      let pgid = workerData.programId || null;
      setProjectId(pid);
      setProgramId(pgid);

      // ワークフロー一覧取得
      if (pid) {
        wfData = await fetchWorkflowsByProject(pid);
      } else if (pgid) {
        wfData = await fetchWorkflowsByProgram(pgid);
      }
      setWorkflows(Array.isArray(wfData) ? wfData : []);

      // アクションプラン一覧取得（プロジェクト/プログラムの associatedActionPlans）
      try {
        let parentData = null;
        if (pid) {
          parentData = await fetchProject(pid);
        } else if (pgid) {
          parentData = await fetchProgram(pgid);
        }
        const plans = parentData?.associatedActionPlans ?? [];
        setActionPlans(Array.isArray(plans) ? plans : []);
      } catch {
        // アクションプラン取得失敗は無視（ワークフロー作成時に警告が出る）
      }
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── ワークフロー削除 ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWorkflow(deleteTarget.workflowId);
      notify.success(intl.formatMessage({ id: "workflow.deleteSuccess" }));
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`)}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "workflow.list.title" })}
          </Typography>
          {worker?.workerName && (
            <Typography variant="body2" color="text.secondary">
              {worker.workerName}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => { setEditTarget(null); setFormOpen(true); }}
        >
          {intl.formatMessage({ id: "workflow.add.button" })}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : workflows.length === 0 ? (
        <Typography color="text.secondary">
          {intl.formatMessage({ id: "workflow.noWorkflows" })}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "workflow.name" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "workflow.startDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "workflow.endDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "workflow.status" })}
                </TableCell>
                <TableCell sx={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {workflows.map((wf) => (
                <TableRow
                  key={wf.workflowId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      `/workers/${workerId}/workflows/${wf.workflowId}`,
                      { state: { workflow: wf } }
                    )
                  }
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {wf.workflowName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(wf.startDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(wf.endDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(wf.businessState)}
                      color={STATUS_COLORS[wf.businessState] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title={intl.formatMessage({ id: "common.edit" })}>
                      <IconButton
                        size="small"
                        onClick={() => { setEditTarget(wf); setFormOpen(true); }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: "common.delete" })}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(wf)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 追加/編集ダイアログ */}
      <WorkflowFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); loadData(); }}
        workflow={editTarget}
        projectId={projectId}
        programId={programId}
        actionPlans={actionPlans}
      />

      {/* 削除確認ダイアログ */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {intl.formatMessage({ id: "workflow.deleteConfirm.title" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(
              { id: "workflow.deleteConfirm.message" },
              { workflowName: deleteTarget?.workflowName || "" }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            {intl.formatMessage({ id: "common.delete" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
